import { useState, useEffect } from 'react';
import { getProducts } from 'services/medusa/client';
import { isProductInStock } from 'utils/stock';

function transformProduct(medusaProduct) {
  const variants = medusaProduct.variants?.map(variant => {
    const price = variant.prices?.find(p =>
      p.currency_code === 'mxn' || p.currency_code === 'MXN'
    );
    const optionValue = variant.options?.[0]?.value || variant.title;
    return {
      label: optionValue,
      value: variant.id,
      id: variant.id,
      percentage: optionValue.includes('%') ? optionValue : null,
      image: variant.thumbnail || medusaProduct.thumbnail,
      price: price ? price.amount : 0,
      originalPrice: null,
      // Precio del programa de distribuidoras (si attachDistributorPrices lo anexó).
      distributor_amount:
        typeof variant.distributor_amount === 'number'
          ? variant.distributor_amount
          : null,
    };
  }) || [];

  const basePrice = variants[0]?.price || 0;
  const badge = medusaProduct.metadata?.badge || null;
  const categories = medusaProduct.categories?.map(c => c.handle) || [];
  const displayCategory = categories.find(c => c !== 'todo') || categories[0] || 'todo';
  const tags = medusaProduct.tags?.map(t => t.value) || [];

  // Orden manual desde Medusa admin (metadata.orden = número).
  // Productos sin "orden" caen al final.
  const ordenRaw = medusaProduct.metadata?.orden;
  const ordenNum = ordenRaw !== undefined && ordenRaw !== null && ordenRaw !== ''
    ? Number(ordenRaw)
    : NaN;
  const orden = Number.isFinite(ordenNum) ? ordenNum : null;

  return {
    id: medusaProduct.id,
    handle: medusaProduct.handle,
    name: medusaProduct.title,
    description: medusaProduct.metadata?.subtitle || medusaProduct.subtitle || '',
    price: basePrice,
    originalPrice: null,
    image: medusaProduct.thumbnail || medusaProduct.images?.[0]?.url,
    category: displayCategory,
    categories: categories,
    tags: tags,
    badge: badge,
    orden: orden,
    inStock: isProductInStock(medusaProduct),
    variants: variants.length > 0 ? variants : undefined,
  };
}

// filterParams: { skinTypes, concerns, ingredients, categoryHandle }
export function useProducts(filterParams = {}) {
  const [allProducts, setAllProducts] = useState([]);   // carga inicial completa (para availableTags)
  const [products, setProducts]       = useState([]);   // resultado filtrado actual
  const [tagValueToId, setTagValueToId] = useState({}); // mapa tagValue → tagId (para server-side)
  const [isLoading, setIsLoading]     = useState(true);
  const [error, setError]             = useState(null);

  const {
    skinTypes = [],
    concerns = [],
    ingredients = [],
    categoryHandle = 'todo',
  } = filterParams;

  // Carga inicial — fetch completo una sola vez
  useEffect(() => {
    getProducts()
      .then(raw => {
        const transformed = raw.map(transformProduct);
        const map = {};
        raw.forEach(p => p.tags?.forEach(t => { map[t.value] = t.id; }));
        setAllProducts(transformed);
        setProducts(transformed);
        setTagValueToId(map);
        setIsLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setIsLoading(false);
      });
  }, []);

  // Re-fetch server-side cuando cambian los filtros
  useEffect(() => {
    if (Object.keys(tagValueToId).length === 0) return; // esperar carga inicial

    const allSelectedValues = [...skinTypes, ...concerns, ...ingredients];
    const hasFilters = allSelectedValues.length > 0 || categoryHandle !== 'todo';

    if (!hasFilters) {
      setProducts(allProducts);
      return;
    }

    setIsLoading(true);
    const tagIds = allSelectedValues.map(v => tagValueToId[v]).filter(Boolean);

    getProducts({ tagIds, categoryHandle })
      .then(raw => {
        const transformed = raw.map(transformProduct);

        // AND entre grupos — client-side sobre el resultado reducido del servidor
        let filtered = transformed;
        if (skinTypes.length > 0)
          filtered = filtered.filter(p => p.tags?.some(t => skinTypes.includes(t)));
        if (concerns.length > 0)
          filtered = filtered.filter(p => p.tags?.some(t => concerns.includes(t)));
        if (ingredients.length > 0)
          filtered = filtered.filter(p => p.tags?.some(t => ingredients.includes(t)));

        setProducts(filtered);
        setIsLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setIsLoading(false);
      });
  }, [
    skinTypes,
    concerns,
    ingredients,
    categoryHandle,
    tagValueToId,
    allProducts,
  ]);

  return { products, allProducts, isLoading, error };
}
